'use client';
import Image from 'next/image';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { pushAnalyticsEvent } from '@/lib/analyticsClientSide';
import { DialogDefaultProps } from './infra/types';

const LOCALSTORAGE_KEY = 'haven_update_availability_dialog_shown';

export function useShouldShowUserAvailabilityNudgeDialog():
  | {
      loading: false;
      shouldShow: true;
      props: object;
    }
  | {
      loading: false;
      shouldShow: false;
    } {
  if (
    typeof window !== 'undefined' &&
    !localStorage.getItem('haven_update_availability_dialog_shown')
  ) {
    return { loading: false, shouldShow: true, props: {} };
  }
  return { loading: false, shouldShow: false };
}

export default function UserAvailabilityNudgeDialog({
  open,
  onOpenChange,
}: DialogDefaultProps) {
  const t = useTranslations('UserAvailabilityNudgeDialog');
  const { data: session } = useSession();
  const accountType = session?.user?.accountType;

  useEffect(() => {
    if (open) {
      pushAnalyticsEvent('user availability - showNudgeDialog');
    }
  }, [open]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      localStorage.setItem(LOCALSTORAGE_KEY, 'true');
      pushAnalyticsEvent('user availability - closedNudgeDialog');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <Image
            src='/images/user-availability.svg'
            alt='User Availability'
            width={250}
            height={164}
            className='mx-auto'
          />
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>
            {t('text', { accountType: accountType ?? '' })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <div className='flex w-full gap-4 [&>*]:w-full'>
            <Button
              onClick={() => {
                handleOpenChange(false);
              }}
              size='medium'
            >
              <Link href={`/edit-profile?focusOn=availability`}>
                {t('addAvailability')}
              </Link>
            </Button>
            <Button
              onClick={() => handleOpenChange(false)}
              size='medium'
              variant='tertiary'
            >
              {t('cancel')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
